// Per-user editor for the quick-log tile grid. Hide / show tiles and reorder
// them via up/down arrows (works on touch + desktop without a drag library).

import { useEffect, useState } from 'react';
import { TILE_IDS, type TileId } from '@shared/constants.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, ToggleSwitch } from './SheetShell.tsx';
import { ALL_TILES, DEFAULT_TILES, type Tile } from '../home/ButtonGrid.tsx';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Current saved order (visible). May be a subset of TILE_IDS. */
  value: TileId[] | null;
  mergeBreast: boolean;
  onSave: (tiles: TileId[]) => Promise<unknown> | void;
};

const TILE_BY_ID = new Map<TileId, Tile>();
for (const t of ALL_TILES) TILE_BY_ID.set(t.id, t);
// Add a synthetic "Breast" tile for the merged variant
TILE_BY_ID.set('breast', {
  id: 'breast',
  label: 'Breast (merged)',
  accent: 'breast',
  icon: (p) => <Icon.breast {...p} />,
});

export function QuickLogEditorSheet({ open, onClose, value, mergeBreast, onSave }: Props) {
  // Available pool: every tile id, with bL/bR or 'breast' filtered based on
  // the merge setting (one or the other, never both).
  const allTiles = (TILE_IDS as readonly TileId[]).filter((id) => {
    if (mergeBreast) return id !== 'bL' && id !== 'bR';
    return id !== 'breast';
  });

  const [order, setOrder] = useState<TileId[]>([]);
  const [hidden, setHidden] = useState<Set<TileId>>(new Set());

  useEffect(() => {
    if (!open) return;
    const visible = (value && value.length ? value : DEFAULT_TILES.map((t) => t.id)).filter((id) =>
      allTiles.includes(id),
    ) as TileId[];
    const visSet = new Set(visible);
    const remaining = allTiles.filter((id) => !visSet.has(id));
    setOrder([...visible, ...remaining]);
    setHidden(new Set(remaining));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mergeBreast]);

  function move(idx: number, dir: -1 | 1) {
    const next = [...order];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  }
  function toggle(id: TileId) {
    setHidden((h) => {
      const n = new Set(h);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title="Edit quick log"
      tagline="Reorder or hide tiles"
      accent="accent"
      icon={(p) => <Icon.edit {...p} />}
      primaryLabel="Save"
      secondaryLabel="Cancel"
      onPrimary={async () => {
        const visible = order.filter((id) => !hidden.has(id));
        await onSave(visible);
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {order.map((id, i) => {
          const meta = TILE_BY_ID.get(id);
          if (!meta) return null;
          const isHidden = hidden.has(id);
          const Icn = meta.icon;
          return (
            <div
              key={id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 14,
                opacity: isHidden ? 0.55 : 1,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  background: `color-mix(in oklab, var(--${meta.accent}) 18%, transparent)`,
                  color: `var(--${meta.accent})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icn s={18} strokeWidth={1.8} />
              </div>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{meta.label}</div>
              <button
                type="button"
                aria-label="Move up"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'var(--bg-sunken)',
                  color: 'var(--text-2)',
                  opacity: i === 0 ? 0.35 : 1,
                }}
              >
                <Icon.chevU s={14} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                aria-label="Move down"
                onClick={() => move(i, 1)}
                disabled={i === order.length - 1}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'var(--bg-sunken)',
                  color: 'var(--text-2)',
                  opacity: i === order.length - 1 ? 0.35 : 1,
                }}
              >
                <Icon.chevD s={14} strokeWidth={2.2} />
              </button>
              <ToggleSwitch on={!isHidden} onChange={() => toggle(id)} />
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', padding: '8px 4px 0' }}>
        Hidden tiles drop to the bottom and won't appear on Home.
      </div>
    </SheetShell>
  );
}
